import React, { useCallback, useEffect, useState } from 'react';
import { ShareIcon, RefreshCw } from 'lucide-react';
import { promotionsApi, type MemoryPromotion } from '@/services/api/agentApi';
import { PromotionCard } from './PromotionCard';
import { Button } from '@/components/ui/button';

const STATUS_TABS: Array<{ value: string; label: string }> = [
  { value: 'pending_review', label: 'Pending' },
  { value: 'auto_approved', label: 'Auto-approved' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

export const PromotionsTab: React.FC = () => {
  const [scope, setScope] = useState<'mine' | 'org'>('mine');
  const [status, setStatus] = useState<string>('pending_review');
  const [items, setItems] = useState<MemoryPromotion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await promotionsApi.list(scope, status);
      setItems(res.data ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [scope, status]);

  useEffect(() => { void refresh(); }, [refresh]);

  const onApprove = async (id: string) => { await promotionsApi.approve(id); await refresh(); };
  const onReject = async (id: string) => { await promotionsApi.reject(id); await refresh(); };
  const onRevoke = async (id: string) => { await promotionsApi.revoke(id); await refresh(); };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShareIcon className="h-5 w-5" />
          <h2 className="text-lg font-semibold">Memory Promotions</h2>
          <span className="text-xs text-muted-foreground">
            Memories your agent wants to share with your organization
          </span>
        </div>
        <Button size="sm" variant="ghost" onClick={() => void refresh()} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <div className="text-xs text-muted-foreground mr-2">Scope:</div>
        <Button size="sm" variant={scope === 'mine' ? 'default' : 'outline'} onClick={() => setScope('mine')}>
          From me
        </Button>
        <Button size="sm" variant={scope === 'org' ? 'default' : 'outline'} onClick={() => setScope('org')}>
          Into my org
        </Button>
      </div>

      <div className="flex items-center gap-1 mb-4 border-b">
        {STATUS_TABS.map(t => (
          <button
            key={t.value}
            onClick={() => setStatus(t.value)}
            className={`px-3 py-1.5 text-sm border-b-2 transition-colors ${
              status === t.value
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="text-sm text-red-600 mb-3">Failed to load: {error}</div>
      )}

      {!loading && items.length === 0 && (
        <div className="text-sm text-muted-foreground py-12 text-center">
          No promotions in this view.
        </div>
      )}

      {items.map(p => (
        <PromotionCard
          key={p.promotion_id}
          promotion={p}
          onApprove={onApprove}
          onReject={onReject}
          onRevoke={onRevoke}
        />
      ))}
    </div>
  );
};
